<script>
    // Licensed under Gnu Public License version 2
    // Copyright (c) 2020-2024 (original work) Open Assessment Technologies SA ;
    import { createEventDispatcher } from 'svelte';
    import { calculateAbsolutePosition, getActualKey, getParentDimensions } from '@oat-sa-private/ui-core';
    import { breakpoints } from '@oat-sa-private/ui-identity';
    import FlyoutMenuItem from './FlyoutMenuItem.svelte';

    const dispatch = createEventDispatcher();

    export let open = false;
    export let getBoundingParentRect;
    // prevent "onClose re-opening" - when you click outside the Flyout, causing close,
    // but on the caller, causing immediate re-open
    let closing = false;
    $: open = !closing && open;

    let pointer = 0;

    /**
     * @example of menu items array format:
     * [{
     *    key: 'highlight',
     *    label: 'Text Highlighter',
     *    icon: 'highlighter-16',
     *    togglable: true,
     *    checked: false,
     *    disabled: false,
     *    actionFocusTarget: '#some-element'
     * }]
     */
    export let items = [];

    // Calculate column-hiding properties based on items content
    $: hasToggleColumn = items.some(item => item.togglable);
    $: hasIconColumn = items.some(item => item.icon);

    // For anchored positioning
    export let caller;
    export let autoFocus = true;
    export let preventScrollOnFocus = true;
    export let anchoring = 'top left';

    if (!(caller instanceof HTMLElement)) {
        caller = null;
    }
    if (anchoring && !anchoring.match(/^(top|bottom) (left|right)$/)) {
        anchoring = 'top left';
    }

    // DOM elements
    let flyoutMenuElement;

    // Style holder
    let absoluteStyle = '';

    let windowWidth;

    // When opening, focus menu's first button
    $: if (open && autoFocus && flyoutMenuElement?.children.length) {
        flyoutMenuElement.querySelector('button').focus({ preventScroll: preventScrollOnFocus });
    }

    // Positioning logic: sets the absoluteStyle string
    $: {
        if (caller && anchoring && flyoutMenuElement) {
            const anchorRect = caller.getBoundingClientRect();
            const { scrollTop, scrollLeft, clientWidth, clientHeight } = getParentDimensions(flyoutMenuElement);
            let { top, left, bottom, right } = calculateAbsolutePosition({
                anchorRect,
                documentDimensions: { scrollTop, scrollLeft, clientWidth, clientHeight },
                anchoring,
                offset: {
                    x: -8,
                    y: -4
                }
            });

            // Modification for mobile: position anchored side relative to viewport
            if (windowWidth <= breakpoints.width.small) {
                if (anchoring.endsWith('left')) {
                    left = '8px';
                } else {
                    right = '8px';
                }
            }

            absoluteStyle = `top:${top}; bottom:${bottom}; left:${left}; right:${right}; transform:none ; position:absolute;`;
        } else if (getBoundingParentRect && flyoutMenuElement && !absoluteStyle) {
            const isOutOfLowerBound = flyoutMenuElement.getBoundingClientRect().bottom > getBoundingParentRect().bottom;

            if (isOutOfLowerBound) {
                absoluteStyle = 'bottom: 100%; top:auto';
            }
        } else {
            absoluteStyle = '';
        }
    }

    /**
     * Handle the click or keypress on a menu item
     * @param {CustomEvent} event
     * @fires action
     */
    function handleItemAction(event) {
        const actionedItem = items.find(item => item.key === event.detail.key);
        if (actionedItem) {
            if (!actionedItem.togglable) {
                close();
                redirectFocus(actionedItem.actionFocusTarget);
            }
            dispatch('action', { key: actionedItem.key });
        }
    }

    /**
     * Determine whether to close the menu or not, when focused element changes
     */
    function handleFocusout() {
        setTimeout(() => {
            // close this menu only if focus was really transferred outside
            if (open && flyoutMenuElement && !flyoutMenuElement.contains(document.activeElement)) {
                close();
                redirectFocus();
            }
        }, 150);
    }

    /**
     * Handle key presses on the menu
     * (Tab, Enter, Space are still handled by default browser behaviours)
     * @param {KeyboardEvent} event
     */
    function handleKeydown(event) {
        const keyname = getActualKey(event);
        switch (keyname) {
            case 'esc':
                close();
                redirectFocus();
                break;
            case 'up':
                pointerBackward();
                break;
            case 'down':
                pointerForward();
                break;
        }
    }

    /**
     * Move pointer to next item if it is present
     * Skip disabled items
     */
    function pointerForward() {
        pointer = Math.min(items.length - 1, pointer + 1);
        if (items[pointer].disabled) {
            // advance pointer if there's next option, and return pointer back if there are no such option
            if (pointer + 1 <= items.length - 1) {
                pointerForward();
            } else {
                pointerBackward();
            }
        }
    }

    /**
     * Move pointer to previous item if it is present
     * Skip disabled items
     */
    function pointerBackward() {
        pointer = Math.max(pointer - 1, 0);

        if (items[pointer].disabled) {
            if (pointer - 1 >= 0) {
                pointerBackward();
            } else {
                pointerForward();
            }
        }
    }

    /**
     * Close this menu
     */
    function close() {
        open = false;
        pointer = 0;

        // temporary closing state prevents accidental immediate re-opening
        closing = true;
        setTimeout(() => {
            closing = false;
        }, 250);
    }

    /**
     * Deal with focus redirection onto a specified element
     * or, by default, onto the caller element
     * @param {String} targetSelector
     */
    function redirectFocus(targetSelector = null) {
        const targetElement = document.querySelector(targetSelector);

        if (targetElement) {
            targetElement.focus({ preventScroll: preventScrollOnFocus });
        } else if (caller && autoFocus) {
            let childButton = caller.querySelector('button'); // first focusable child of caller
            if (childButton) {
                childButton.focus({ preventScroll: preventScrollOnFocus });
            }
        }
    }
</script>

<style>
    .flyout-menu {
        max-width: 33rem;
        background: var(--color-bg-default);
        border: var(--border-thin) solid var(--color-border-disabled);
        box-shadow: 0 0 0.75rem 0.375rem rgba(0, 0, 0, 0.1);
        outline: none;
        position: absolute;
        z-index: var(--layer-4);

        & :global(ol) {
            list-style: none;
            margin: 0;
            padding: 0;
        }
    }

    .slot-wrapper {
        padding: var(--space-1x5);
        &.before {
            border-bottom: var(--border-thin) solid var(--color-text-inverted-alternative);
        }
        &.after {
            border-top: var(--border-thin) solid var(--color-text-inverted-alternative);
        }
    }
</style>

<svelte:window bind:innerWidth={windowWidth} />

{#if open}
    <div class="flyout-menu" style={absoluteStyle} bind:this={flyoutMenuElement} on:focusout={handleFocusout}>
        {#if $$slots.before}
            <div class="slot-wrapper before">
                <slot name="before" />
            </div>
        {/if}
        <ol role="menu" on:keydown|stopPropagation={handleKeydown}>
            {#each items as item (item.key)}
                <FlyoutMenuItem
                    {...item}
                    active={items[pointer] === item}
                    {hasToggleColumn}
                    {hasIconColumn}
                    {preventScrollOnFocus}
                    on:action={handleItemAction} />
            {/each}
        </ol>
        {#if $$slots.after}
            <div class="slot-wrapper after">
                <slot name="after" />
            </div>
        {/if}
    </div>
{/if}
